import { queryOptions, useQuery } from '@tanstack/react-query';

import { GraphQLClient, gql } from '@tf/api';
import type { IdenfyResponse } from '@tf/shared/lib/components/VerificationResultModal/types';

export type LivenessCheck = {
	id: string;
	meta: {
		service_kind: 'idenfy';
		state: 'COMPLETED' | 'FAILED' | 'LIMITED' | 'NEW';
	};
	data: {
		media?: {
			record_attachment: {
				_object: string;
				_relationKind: 'record_attachment';
			};
		}[];
		response?: IdenfyResponse;
	};
	attachments: {
		object: {
			id: string;
			meta: {
				'@createdAt': string;
				'@filename': string;
			};
		};
	}[];
};

const livenessCheckQueryOptions = (livenessCheckId: string) => {
	return queryOptions({
		queryKey: ['livenessCheck', livenessCheckId],
		queryFn: async (): Promise<LivenessCheck> => {
			const res = await GraphQLClient.getInstance().request(
				gql`
					query LivenessCheck($livenessCheckId: ID!) {
						get(rootFilter: { id: $livenessCheckId }) {
							id
							meta(path: ["/service_kind", "/state"])
							data(path: ["/media", "/response"])
							attachments: nestedRelation(filter: { relationKind: "record_attachment" }) {
								object {
									id
									meta
								}
							}
						}
					}
				`,
				{ livenessCheckId },
			);
			return res.get[0];
		},
	});
};

export const useLivenessCheck = (livenessCheckId: string) => {
	return useQuery(livenessCheckQueryOptions(livenessCheckId));
};

const profileLivenessCheckQueryOptions = (profileObjectId: string) => {
	return queryOptions({
		queryKey: ['profileLivenessCheck', profileObjectId],
		queryFn: async (): Promise<LivenessCheck | null> => {
			const res = await GraphQLClient.getInstance().request(
				gql`
					query ProfileLivenessCheck($profileObjectId: JSON!) {
						get(
							rootFilter: {
								archetype: "record"
								kind: "profile_liveness_check"
								meta: { AND: [{ key: "/@with/profile", operator: EQ, value: $profileObjectId }] }
							}
						) {
							nestedRelation(filter: { relationKind: "liveness_check" }) {
								object {
									id
									meta(path: ["/service_kind", "/state"])
									data(path: ["/media", "/response"])
									attachments: nestedRelation(filter: { relationKind: "record_attachment" }) {
										object {
											id
											meta
										}
									}
								}
							}
						}
					}
				`,
				{ profileObjectId },
			);

			return res.get[0]?.nestedRelation[0]?.object ?? null;
		},
	});
};

export const useProfileLivenessCheck = (profileObjectId: string) => {
	return useQuery(profileLivenessCheckQueryOptions(profileObjectId));
};
